/**
 * Professional Portal API Service
 */
import type {
  Professional,
  ProfessionalLoginRequest,
  ProfessionalRegisterRequest,
  ReviewRequestListItem,
  AnonymizedReportData,
  SubmitReviewRequest,
} from '../types/professional';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
const TOKEN_KEY = 'marple_professional_token';

export function getProfessionalToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setProfessionalToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearProfessionalToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

class ProfessionalApiClient {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}/professional${endpoint}`;
    const token = getProfessionalToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> || {}),
    };
    const response = await fetch(url, { ...options, headers });
    if (!response.ok) {
      let msg = `Request failed (${response.status})`;
      try {
        const j = await response.json();
        const d = j.detail;
        msg = Array.isArray(d) ? d.map((x: any) => x.msg || String(x)).join('; ') : d || msg;
      } catch { /* ignore */ }
      throw Object.assign(new Error(msg), { status: response.status });
    }
    if (response.status === 204) return undefined as T;
    return response.json();
  }

  get<T>(path: string) { return this.request<T>(path); }
  post<T>(path: string, body: unknown) {
    return this.request<T>(path, { method: 'POST', body: JSON.stringify(body) });
  }
}

const client = new ProfessionalApiClient();

export const professionalApi = {
  register: (data: ProfessionalRegisterRequest) =>
    client.post<{ professional: Professional; message: string }>('/register', data),

  login: (data: ProfessionalLoginRequest) =>
    client.post<{ access_token: string; professional: Professional }>('/login', data),

  getMe: () =>
    client.get<Professional>('/me'),

  listReviewRequests: (params?: { status?: string; limit?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.append('status', params.status);
    if (params?.limit) q.append('limit', params.limit.toString());
    return client.get<ReviewRequestListItem[]>(`/review-requests?${q}`);
  },

  getReviewRequest: (id: string) =>
    client.get<any>(`/review-requests/${id}`),

  acceptReviewRequest: (id: string) =>
    client.post<{ message: string; status: string }>(`/review-requests/${id}/accept`, {}),

  submitReview: (id: string, data: SubmitReviewRequest) =>
    client.post<{ message: string; review_id: string; payout_display: string }>(`/review-requests/${id}/submit`, data),

  getMyReviews: () =>
    client.get<ReviewRequestListItem[]>('/my-reviews'),

  getWallet: () =>
    client.get<{ balance_cents: number; balance_display: string; transactions: any[] }>('/wallet'),
};

export default professionalApi;
